/**
 * DateRangeFilter — v4.4.33
 *
 * Componente reusable para filtrado por rango de fechas con presets.
 *
 * Props:
 *   - value: { desde: string ISO | null, hasta: string ISO | null }
 *   - onChange: (newValue) => void
 *   - label?: string (etiqueta, default "Fecha")
 *   - presets?: array de presets a mostrar (default: todos)
 *   - compact?: boolean (versión compacta sin label)
 *
 * Uso:
 *   const [rango, setRango] = useState({ desde: null, hasta: null });
 *   <DateRangeFilter value={rango} onChange={setRango} label="Plazo" />
 *
 *   // Luego en la query:
 *   if (rango.desde) params.fecha_desde = rango.desde;
 *   if (rango.hasta) params.fecha_hasta = rango.hasta;
 */
import { useState, useMemo } from "react";
import { Calendar, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

// Helper para fechas
const toISO = (d) => d.toISOString().slice(0, 10);
const today = () => new Date();

const PRESETS = {
  hoy: {
    label: "Hoy",
    range: () => {
      const t = today();
      return { desde: toISO(t), hasta: toISO(t) };
    },
  },
  esta_semana: {
    label: "Esta semana",
    range: () => {
      const t = today();
      const inicio = new Date(t);
      inicio.setDate(t.getDate() - t.getDay() + (t.getDay() === 0 ? -6 : 1)); // lunes
      return { desde: toISO(inicio), hasta: toISO(t) };
    },
  },
  este_mes: {
    label: "Este mes",
    range: () => {
      const t = today();
      const inicio = new Date(t.getFullYear(), t.getMonth(), 1);
      return { desde: toISO(inicio), hasta: toISO(t) };
    },
  },
  ultimos_30d: {
    label: "Últimos 30 días",
    range: () => {
      const t = today();
      const inicio = new Date(t);
      inicio.setDate(t.getDate() - 30);
      return { desde: toISO(inicio), hasta: toISO(t) };
    },
  },
  ultimos_3m: {
    label: "Últimos 3 meses",
    range: () => {
      const t = today();
      const inicio = new Date(t);
      inicio.setMonth(t.getMonth() - 3);
      return { desde: toISO(inicio), hasta: toISO(t) };
    },
  },
  ultimos_6m: {
    label: "Últimos 6 meses",
    range: () => {
      const t = today();
      const inicio = new Date(t);
      inicio.setMonth(t.getMonth() - 6);
      return { desde: toISO(inicio), hasta: toISO(t) };
    },
  },
  ultimo_anio: {
    label: "Último año",
    range: () => {
      const t = today();
      const inicio = new Date(t);
      inicio.setFullYear(t.getFullYear() - 1);
      return { desde: toISO(inicio), hasta: toISO(t) };
    },
  },
  proximos_7d: {
    label: "Próximos 7 días",
    range: () => {
      const t = today();
      const fin = new Date(t);
      fin.setDate(t.getDate() + 7);
      return { desde: toISO(t), hasta: toISO(fin) };
    },
  },
  proximos_30d: {
    label: "Próximos 30 días",
    range: () => {
      const t = today();
      const fin = new Date(t);
      fin.setDate(t.getDate() + 30);
      return { desde: toISO(t), hasta: toISO(fin) };
    },
  },
};

// Presets por defecto (para uso histórico, p.ej. detección/publicación/adjudicación)
const DEFAULT_PRESETS_HISTORICO = [
  "hoy",
  "esta_semana",
  "este_mes",
  "ultimos_30d",
  "ultimos_3m",
  "ultimos_6m",
  "ultimo_anio",
];

// Presets para PLAZOS (futuras fechas, ej. fecha_limite_presentacion)
export const PRESETS_PLAZO = [
  "hoy",
  "proximos_7d",
  "proximos_30d",
];

export default function DateRangeFilter({
  value = { desde: null, hasta: null },
  onChange,
  label = "Fecha",
  presets = DEFAULT_PRESETS_HISTORICO,
  compact = false,
}) {
  const [open, setOpen] = useState(false);

  const aplicarPreset = (presetKey) => {
    const r = PRESETS[presetKey].range();
    onChange(r);
    setOpen(false);
  };

  const limpiar = () => {
    onChange({ desde: null, hasta: null });
  };

  const activePresetLabel = useMemo(() => {
    if (!value?.desde && !value?.hasta) return null;
    // Detectar si coincide con algún preset
    for (const key of presets) {
      const r = PRESETS[key].range();
      if (r.desde === value.desde && r.hasta === value.hasta) {
        return PRESETS[key].label;
      }
    }
    return null;
  }, [value, presets]);

  const displayText = useMemo(() => {
    if (!value?.desde && !value?.hasta) return label;
    if (activePresetLabel) return activePresetLabel;
    if (value.desde && value.hasta) {
      const formatFecha = (iso) => {
        const [y, m, d] = iso.split("-");
        return `${d}/${m}/${y.slice(2)}`;
      };
      return `${formatFecha(value.desde)} → ${formatFecha(value.hasta)}`;
    }
    if (value.desde) return `Desde ${value.desde}`;
    if (value.hasta) return `Hasta ${value.hasta}`;
    return label;
  }, [value, label, activePresetLabel]);

  const hasValue = !!(value?.desde || value?.hasta);

  return (
    <div className={compact ? "" : "flex flex-col gap-1"}>
      {!compact && (
        <label className="text-xs theme-text-muted block">{label}</label>
      )}
      <div className="flex gap-1.5">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className={`theme-bg-tertiary theme-border theme-text justify-start gap-2 min-w-[180px] ${
                hasValue ? "border-teal-500/50 text-teal-700 dark:text-teal-300" : ""
              }`}
            >
              <Calendar className="w-4 h-4" />
              <span className="truncate">{displayText}</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-3">
            <div className="text-xs theme-text-muted uppercase mb-2">
              Presets rápidos
            </div>
            <div className="grid grid-cols-2 gap-1.5 mb-3">
              {presets.map((key) => (
                <Button
                  key={key}
                  variant="ghost"
                  size="sm"
                  className="justify-start text-xs h-8"
                  onClick={() => aplicarPreset(key)}
                >
                  {PRESETS[key].label}
                </Button>
              ))}
            </div>
            <div className="text-xs theme-text-muted uppercase mb-2">
              Rango personalizado
            </div>
            <div className="space-y-2">
              <div>
                <label className="text-xs theme-text-muted block mb-1">Desde</label>
                <Input
                  type="date"
                  value={value?.desde || ""}
                  onChange={(e) =>
                    onChange({ ...value, desde: e.target.value || null })
                  }
                  className="theme-bg-tertiary theme-border theme-text"
                />
              </div>
              <div>
                <label className="text-xs theme-text-muted block mb-1">Hasta</label>
                <Input
                  type="date"
                  value={value?.hasta || ""}
                  onChange={(e) =>
                    onChange({ ...value, hasta: e.target.value || null })
                  }
                  className="theme-bg-tertiary theme-border theme-text"
                />
              </div>
            </div>
            <div className="mt-3 flex justify-end">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  limpiar();
                  setOpen(false);
                }}
                disabled={!hasValue}
              >
                Limpiar
              </Button>
            </div>
          </PopoverContent>
        </Popover>
        {hasValue && (
          <Button
            variant="ghost"
            size="sm"
            onClick={limpiar}
            className="px-2"
            title="Quitar filtro de fecha"
          >
            <X className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
}
